import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import { useFormTemplates } from "../../context/FormTemplatesContext";
import { useServiceRequiredForms } from "../../context/ServiceRequiredFormsContext";
import {
  AssignFormPayload,
  FormTemplate,
  PendingRequiredForm,
  RequiredForm,
} from "../../types/formTemplates";
import { AssignFormModal } from "./AssignFormModal";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

interface RequiredFormsSectionProps {
  salonServiceId: string;
  readOnly?: boolean;
  pendingForms?: PendingRequiredForm[];
  onAddPendingForm?: (form: PendingRequiredForm) => void;
  onRemovePendingForm?: (tempId: string) => void;
}

interface ResolvedRequirement {
  requirement: RequiredForm;
  template: FormTemplate | null;
}

export const RequiredFormsSection: React.FC<RequiredFormsSectionProps> = ({
  salonServiceId,
  readOnly = false,
  pendingForms,
  onAddPendingForm,
  onRemovePendingForm,
}) => {
  const { standardTemplates, customTemplates, fetchTemplates } =
    useFormTemplates();
  const { listRequiredForms, linkRequiredForm, unlinkRequiredForm } =
    useServiceRequiredForms();

  const [requirements, setRequirements] = useState<RequiredForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [pendingUnlink, setPendingUnlink] = useState<{
    req: RequiredForm;
    name: string;
  } | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const refresh = useCallback(async () => {
    if (!salonServiceId) return;
    setIsLoading(true);
    try {
      const list = await listRequiredForms(salonServiceId);
      setRequirements(list);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not load required forms",
      });
    } finally {
      setIsLoading(false);
    }
  }, [salonServiceId, listRequiredForms]);

  useEffect(() => {
    refresh();
    fetchTemplates().catch(() => {});
  }, [refresh, fetchTemplates]);

  const resolved: ResolvedRequirement[] = useMemo(() => {
    return requirements.map((r) => {
      const pool =
        r.source_type === "salon_template"
          ? customTemplates
          : standardTemplates;
      const template =
        pool.find((t) => (t.slug || t.id) === r.source_id) ?? null;
      return { requirement: r, template };
    });
  }, [requirements, customTemplates, standardTemplates]);

  // Composite identity keys for templates already attached to the service
  // (saved requirements + unsaved pending forms). Passed to the picker so
  // attached templates are hidden and can't be added twice.
  const attachedKeys = useMemo(() => {
    const keys = new Set<string>();
    requirements.forEach((r) => keys.add(`${r.source_type}:${r.source_id}`));
    pendingForms?.forEach((pf) =>
      keys.add(`${pf.source_type}:${pf.source_id}`),
    );
    return keys;
  }, [requirements, pendingForms]);

  const handleLink = async (payloads: AssignFormPayload[]) => {
    if (payloads.length === 0) return;
    if (onAddPendingForm && !salonServiceId) {
      const allTemplates = [...standardTemplates, ...customTemplates];
      payloads.forEach((payload) => {
        const match = allTemplates.find(
          (t) =>
            (t.slug || t.id) === payload.source_id &&
            (t.source === "custom" ? "salon_template" : "template") ===
              payload.source_type,
        );
        onAddPendingForm({
          tempId: crypto.randomUUID(),
          source_type: payload.source_type,
          source_id: payload.source_id,
          name: match?.name ?? payload.source_id,
        });
      });
      setPickerOpen(false);
      return;
    }
    setIsLinking(true);
    try {
      // Attempt every link so one failure doesn't abandon the rest. We always
      // refresh() afterwards (finally) so `requirements` / `attachedKeys`
      // reflect whatever actually got linked — otherwise a partial failure
      // would leave the picker offering already-attached forms (→ 409 on retry).
      const results = await Promise.allSettled(
        payloads.map((payload) => linkRequiredForm(salonServiceId, payload)),
      );
      const failures = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const successCount = results.length - failures.length;

      if (failures.length === 0) {
        notifySuccess(
          payloads.length > 1
            ? `${payloads.length} templates are now required for the service.`
            : "This template is now required for the service.",
          {
            title:
              payloads.length > 1
                ? "Required forms added"
                : "Required form added",
          },
        );
        setPickerOpen(false);
      } else if (successCount === 0) {
        const reason = failures[0].reason;
        notifyError(
          reason instanceof Error ? reason.message : "Unknown error.",
          {
            title:
              payloads.length > 1
                ? "Could not add required forms"
                : "Could not add required form",
          },
        );
        // Keep the picker open so the user can retry.
      } else {
        // Partial success: keep the picker open. After refresh() the linked
        // forms drop out of the picker and the failed ones remain selectable.
        notifyWarning(
          `Added ${successCount} of ${payloads.length}. ${failures.length} could not be added — please retry the remaining.`,
          { title: "Some forms were not added" },
        );
      }
    } finally {
      await refresh();
      setIsLinking(false);
    }
  };

  const handleUnlink = (req: RequiredForm, name: string) => {
    setPendingUnlink({ req, name });
  };

  const confirmUnlink = async () => {
    if (!pendingUnlink) return;
    setIsUnlinking(true);
    try {
      await unlinkRequiredForm(salonServiceId, pendingUnlink.req.id);
      notifySuccess("The requirement was unlinked from this service.", {
        title: "Required form removed",
      });
      setPendingUnlink(null);
      await refresh();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not remove required form",
      });
    } finally {
      setIsUnlinking(false);
    }
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Box>
          <Title order={6}>Required forms</Title>
          <Text size="xs" c="dimmed">
            Forms clients must complete for this service.
          </Text>
        </Box>
        {!readOnly && (
          <Button
            size="xs"
            variant="default"
            leftSection={<IconPlus size={14} />}
            onClick={() => setPickerOpen(true)}
          >
            Add required form
          </Button>
        )}
      </Group>

      {isLoading ? (
        <Group justify="center" py="sm">
          <Loader size="xs" />
        </Group>
      ) : resolved.length === 0 &&
        (!pendingForms || pendingForms.length === 0) ? (
        <Alert
          color="gray"
          icon={<IconAlertCircle size={14} />}
          variant="light"
        >
          No required forms yet. Add one to associate a template with this
          service.
        </Alert>
      ) : (
        <Stack gap="xs">
          {resolved.map(({ requirement, template }) => {
            const displayName =
              template?.name ??
              `${requirement.source_type === "template" ? "Standard" : "Custom"} form (${requirement.source_id})`;
            return (
              <Card key={requirement.id} withBorder padding="xs" radius="sm">
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>
                      {displayName}
                    </Text>
                    <Badge
                      size="xs"
                      variant="light"
                      color={
                        requirement.source_type === "salon_template"
                          ? "burntOrange"
                          : "teal"
                      }
                    >
                      {requirement.source_type === "salon_template"
                        ? "Custom"
                        : "Standard"}
                    </Badge>
                  </Group>
                  {!readOnly && (
                    <Tooltip label="Remove">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleUnlink(requirement, displayName)}
                        aria-label="Remove required form"
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Card>
            );
          })}
          {pendingForms?.map((pf) => (
            <Card key={pf.tempId} withBorder padding="xs" radius="sm">
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {pf.name}
                  </Text>
                  <Badge
                    size="xs"
                    variant="light"
                    color={
                      pf.source_type === "salon_template"
                        ? "burntOrange"
                        : "teal"
                    }
                  >
                    {pf.source_type === "salon_template"
                      ? "Custom"
                      : "Standard"}
                  </Badge>
                </Group>
                {onRemovePendingForm && (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => onRemovePendingForm(pf.tempId)}
                    aria-label="Remove pending form"
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      )}

      <AssignFormModal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAssign={handleLink}
        isAssigning={isLinking}
        attachedKeys={attachedKeys}
      />

      <DeleteConfirmationModal
        isOpen={pendingUnlink !== null}
        onClose={() => {
          if (!isUnlinking) setPendingUnlink(null);
        }}
        onConfirm={confirmUnlink}
        title="Remove required form"
        message={
          pendingUnlink
            ? `Remove "${pendingUnlink.name}" as a required form for this service?`
            : ""
        }
        isLoading={isUnlinking}
      />
    </Stack>
  );
};

export default RequiredFormsSection;
